import {announce} from '@github-ui/aria-live'
import {useOptionalEstablishedAhpSession} from '@github-ui/copilot-shared-ui/hooks/use-established-ahp-session'
import {useOptionalEstablishedAhpSessionViewState} from '@github-ui/copilot-shared-ui/hooks/use-established-ahp-session-view'
import type {ResourceReadResult} from '@microsoft/agent-host-protocol'
import {use, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react'

import type {Artifact} from '../../utils/artifacts/artifact-core'
import {AgentsWorkbenchContext} from '../workbench/AgentsWorkbenchContext'
import {isBinary, MAX_PREVIEW_BYTES} from '../workbench/preview-source'
import {beginWorkbenchPreviewRequest, cancelWorkbenchPreviewRequest} from '../workbench/use-workbench-preview-sources'
import {
  buildArtifactPreviewItem,
  buildUnavailableArtifactPreviewItem,
  getArtifactPreviewItemId,
  NO_PREVIEW_REASON,
  useOptionalArtifactContentPreview,
  useSyncOpenArtifactPreviews,
} from './use-open-artifact-preview'

const UNAVAILABLE_REASON = 'Unable to load the latest content for this file.'
const FORMAT_REASON = 'Preview is unavailable for this file format.'
const BINARY_REASON = 'Preview is unavailable for this binary file.'
const TOO_LARGE_REASON = 'This file is too large to preview.'

// A read that fails validation cannot succeed on retry, so it keeps its specific reason.
class ArtifactPreviewError extends Error {
  readonly reason: string

  constructor(reason: string) {
    super(reason)
    this.name = 'ArtifactPreviewError'
    this.reason = reason
  }
}

function openedMessage(artifact: Pick<Artifact, 'fileName'>) {
  return `Opened ${artifact.fileName}.`
}

function artifactContentKey(artifact: Artifact): string | undefined {
  if (!artifact.contentRef || !artifact.contentRefOrigin) return undefined
  return JSON.stringify([
    artifact.contentRefOrigin.environmentId,
    artifact.contentRefOrigin.sessionId,
    artifact.contentRef.uri,
    artifact.contentRef.nonce ?? null,
  ])
}

// Content keys start with their session's JSON prefix, e.g. `["env","session",`.
function sessionKeyPrefix(environmentId: string | undefined, sessionId: string | undefined): string {
  return `${JSON.stringify([environmentId ?? null, sessionId ?? null]).slice(0, -1)},`
}

function decodedBase64Length(data: string): number {
  // atob ignores ASCII whitespace, and padding adds no decoded bytes.
  const encoded = data.replace(/[\t\n\f\r ]/g, '')
  const padding = encoded.endsWith('==') ? 2 : encoded.endsWith('=') ? 1 : 0
  return Math.floor((encoded.length * 3) / 4) - padding
}

function decodeResourceText(result: ResourceReadResult): string {
  if (result.encoding === 'utf-8') return result.data
  if (result.encoding !== 'base64') throw new ArtifactPreviewError(FORMAT_REASON)
  // Check the decoded size before allocating the decoded copies.
  if (decodedBase64Length(result.data) > MAX_PREVIEW_BYTES) throw new ArtifactPreviewError(TOO_LARGE_REASON)

  let bytes: Uint8Array
  try {
    bytes = Uint8Array.from(atob(result.data), character => character.charCodeAt(0))
  } catch {
    throw new ArtifactPreviewError(FORMAT_REASON)
  }
  try {
    return new TextDecoder('utf-8', {fatal: true}).decode(bytes)
  } catch {
    throw new ArtifactPreviewError(BINARY_REASON)
  }
}

type ArtifactRefSource = Pick<Artifact, 'fileName' | 'contentRef'>

// Metadata is checked before reading so a binary ref is never transferred. The
// size hint is only approximate, so size is checked after the read instead.
function refPreflightReason({fileName, contentRef}: ArtifactRefSource): string | undefined {
  return isBinary(fileName, contentRef?.contentType) ? FORMAT_REASON : undefined
}

// Applies the same format, binary, and size limits as other workbench previews.
export function decodeArtifactResource(result: ResourceReadResult, artifact: ArtifactRefSource): string {
  if (isBinary(artifact.fileName, result.contentType ?? artifact.contentRef?.contentType)) {
    throw new ArtifactPreviewError(FORMAT_REASON)
  }
  const content = decodeResourceText(result)
  if (content.includes('\0')) throw new ArtifactPreviewError(BINARY_REASON)
  if (content.length > MAX_PREVIEW_BYTES || new Blob([content]).size > MAX_PREVIEW_BYTES) {
    throw new ArtifactPreviewError(TOO_LARGE_REASON)
  }
  return content
}

function referencedEntries(cache: Map<string, string>, referencedKeys: ReadonlySet<string>): Map<string, string> {
  if ([...cache.keys()].every(key => referencedKeys.has(key))) return cache
  return new Map([...cache].filter(([key]) => referencedKeys.has(key)))
}

export function useArtifactWorkbench(artifacts: Artifact[], historyIsSettled = true, artifactWritesSettled = false) {
  const session = useOptionalEstablishedAhpSession()
  const {status} = useOptionalEstablishedAhpSessionViewState()
  const contentPreview = useOptionalArtifactContentPreview()
  const isInWorkbench = use(AgentsWorkbenchContext)
  const environmentId = session?.identity?.environmentId
  const sessionId = session?.identity?.sessionId
  // Refs stay matched while the relay reconnects so already-read content keeps
  // resolving. Only issuing a new read requires a connected session.
  const readResource = status === 'connected' ? session?.view.readResource : undefined
  const hasContentPreview = contentPreview !== undefined
  const allOpenItems = contentPreview?.allOpenItems
  const contentPreviewRef = contentPreview?.contentPreviewRef
  const conversationScopeId = contentPreview?.conversationScopeId
  const itemSelectionVersion = contentPreview?.itemSelectionVersion
  const disableLoadingState = contentPreview?.disableLoadingState
  const enableLoadingState = contentPreview?.enableLoadingState
  const openItem = contentPreview?.openItem
  const openPreviewPane = contentPreview?.openPreviewPane
  const closePreviewPane = contentPreview?.closePreviewPane
  const updateItem = contentPreview?.updateItem
  const [contentCache, setContentCache] = useState(() => new Map<string, string>())
  // Mirrors contentCache synchronously, so a read that settles before the next
  // render still sees content another read just cached.
  const contentCacheRef = useRef(contentCache)
  const latestItemsRef = useRef(contentPreview?.items)
  const latestOpenItemsRef = useRef(allOpenItems)
  const latestHistoryIsSettledRef = useRef(historyIsSettled)
  const latestArtifactWritesSettledRef = useRef(artifactWritesSettled)
  const attemptedRefsRef = useRef(new Set<string>())
  const requestRef = useRef<ReturnType<typeof beginWorkbenchPreviewRequest> | undefined>(undefined)
  const backgroundGenerationRef = useRef(0)
  const latestRefKeysByPathRef = useRef(new Map<string, string>())
  const latestReferencedKeysRef = useRef(new Set<string>())
  const latestArtifactsByPathRef = useRef(new Map<string, Artifact>())
  const latestSessionKeyPrefixRef = useRef(sessionKeyPrefix(environmentId, sessionId))
  // A path missing from unsettled history may only be outside the partial live
  // window, so only a different key or a settled removal supersedes a read.
  const isReadSuperseded = useCallback((path: string, key: string) => {
    // A read from a previous established session never resolves the current one.
    if (!key.startsWith(latestSessionKeyPrefixRef.current)) return true
    const latestKey = latestRefKeysByPathRef.current.get(path)
    if (latestKey !== undefined) return latestKey !== key
    return latestHistoryIsSettledRef.current || latestArtifactsByPathRef.current.has(path)
  }, [])
  const openArtifactAtRef = useRef<((artifact: Artifact, target: HTMLElement) => Promise<void>) | undefined>(undefined)

  const refKeysByPath = useMemo(() => {
    const keys = new Map<string, string>()
    if (!environmentId || !sessionId) return keys

    for (const artifact of artifacts) {
      if (
        artifact.content !== undefined ||
        !artifact.contentRef ||
        !artifact.contentRefOrigin ||
        artifact.contentRefOrigin.environmentId !== environmentId ||
        artifact.contentRefOrigin.sessionId !== sessionId
      ) {
        continue
      }
      const key = artifactContentKey(artifact)
      if (key) keys.set(artifact.absolutePath, key)
    }
    return keys
  }, [artifacts, environmentId, sessionId])

  // Only refs that can still resolve an artifact keep their cached content.
  const referencedContentKeys = useMemo(() => new Set(refKeysByPath.values()), [refKeysByPath])
  const refPaths = useMemo(() => new Set(refKeysByPath.keys()), [refKeysByPath])

  useLayoutEffect(() => {
    latestRefKeysByPathRef.current = refKeysByPath
    latestReferencedKeysRef.current = referencedContentKeys
    latestSessionKeyPrefixRef.current = sessionKeyPrefix(environmentId, sessionId)
  }, [environmentId, refKeysByPath, referencedContentKeys, sessionId])

  useLayoutEffect(() => {
    latestItemsRef.current = contentPreview?.items
    latestOpenItemsRef.current = allOpenItems
    latestHistoryIsSettledRef.current = historyIsSettled
    latestArtifactWritesSettledRef.current = artifactWritesSettled
  }, [allOpenItems, artifactWritesSettled, contentPreview?.items, historyIsSettled])

  // Unreferenced entries are hidden during render and released by the effect
  // below, which updates state only when an entry is actually dropped. While
  // history is unsettled, a missing artifact may return with the same ref, so
  // nothing is released until settled history confirms the removal.
  const contentByRef = useMemo(
    () => referencedEntries(contentCache, referencedContentKeys),
    [contentCache, referencedContentKeys],
  )

  useEffect(() => {
    if (!historyIsSettled) return
    const pruned = referencedEntries(contentCacheRef.current, referencedContentKeys)
    if (pruned === contentCacheRef.current) return
    contentCacheRef.current = pruned
    setContentCache(pruned)
  }, [historyIsSettled, referencedContentKeys])

  const cacheContent = useCallback((key: string, content: string) => {
    const retained = latestHistoryIsSettledRef.current
      ? referencedEntries(contentCacheRef.current, latestReferencedKeysRef.current)
      : contentCacheRef.current
    const next = new Map(retained).set(key, content)
    contentCacheRef.current = next
    setContentCache(next)
  }, [])

  useEffect(() => {
    if (!historyIsSettled) return
    for (const key of attemptedRefsRef.current) {
      if (!referencedContentKeys.has(key)) attemptedRefsRef.current.delete(key)
    }
  }, [historyIsSettled, referencedContentKeys])

  const resolvedArtifacts = useMemo(
    () =>
      artifacts.map(artifact => {
        if (artifact.content !== undefined) return artifact
        const key = refKeysByPath.get(artifact.absolutePath)
        if (!key) return artifact
        const content = contentByRef.get(key)
        return content === undefined ? artifact : {...artifact, content}
      }),
    [artifacts, contentByRef, refKeysByPath],
  )

  useLayoutEffect(() => {
    latestArtifactsByPathRef.current = new Map(resolvedArtifacts.map(artifact => [artifact.absolutePath, artifact]))
  }, [resolvedArtifacts])

  useLayoutEffect(() => () => requestRef.current?.cancel(), [conversationScopeId, itemSelectionVersion])

  // The provider resets its tabs on a scope change, and the next scope's history
  // may never settle, so release scope-owned reads here instead of waiting.
  useLayoutEffect(
    () => () => {
      backgroundGenerationRef.current += 1
      attemptedRefsRef.current.clear()
      if (contentCacheRef.current.size === 0) return
      contentCacheRef.current = new Map()
      setContentCache(contentCacheRef.current)
    },
    [conversationScopeId],
  )

  // Other preview sources share this request slot, so cancel whichever load is pending.
  const cancelPendingOpen = useCallback(() => {
    if (contentPreviewRef && disableLoadingState) cancelWorkbenchPreviewRequest(contentPreviewRef, disableLoadingState)
  }, [contentPreviewRef, disableLoadingState])

  const canOpenArtifact = useCallback(
    (artifact: Artifact) =>
      hasContentPreview &&
      (isInWorkbench ||
        artifact.content !== undefined ||
        (readResource !== undefined && refKeysByPath.has(artifact.absolutePath))),
    [hasContentPreview, isInWorkbench, readResource, refKeysByPath],
  )

  const openArtifactAt = useCallback(
    async (artifact: Artifact, target: HTMLElement) => {
      if (!openItem || !openPreviewPane || !updateItem) return
      const item = buildArtifactPreviewItem(artifact, new Date(), undefined, {
        catalog: [...latestArtifactsByPathRef.current.values()],
        writesSettled: latestArtifactWritesSettledRef.current,
      })
      if (item) {
        const existingItem = latestOpenItemsRef.current?.includes(item.id)
          ? latestItemsRef.current?.get(item.id)
          : undefined
        if (
          !latestArtifactWritesSettledRef.current &&
          existingItem?.type === 'file' &&
          existingItem.htmlPreview &&
          existingItem.value === artifact.content
        ) {
          item.previewEligible = existingItem.previewEligible
          item.htmlPreview = existingItem.htmlPreview
        }
        if (contentPreviewRef && disableLoadingState) {
          cancelWorkbenchPreviewRequest(contentPreviewRef, disableLoadingState)
        }
        updateItem(item)
        openItem(item.id)
        openPreviewPane(target)
        return
      }

      const key = refKeysByPath.get(artifact.absolutePath)
      if (
        !key ||
        !artifact.contentRef ||
        !readResource ||
        !contentPreviewRef ||
        !disableLoadingState ||
        !enableLoadingState
      ) {
        if (!isInWorkbench) return
        if (contentPreviewRef && disableLoadingState) {
          cancelWorkbenchPreviewRequest(contentPreviewRef, disableLoadingState)
        }
        if (key && !readResource) attemptedRefsRef.current.delete(key)
        const itemId = getArtifactPreviewItemId(artifact)
        const existingItem = latestOpenItemsRef.current?.includes(itemId)
          ? latestItemsRef.current?.get(itemId)
          : undefined
        const keepExistingContent =
          existingItem?.type === 'file' &&
          existingItem.value !== undefined &&
          existingItem.unavailableReason === undefined &&
          (!latestHistoryIsSettledRef.current || (key !== undefined && readResource === undefined))
        if (!keepExistingContent) {
          updateItem(buildUnavailableArtifactPreviewItem(artifact, new Date(), NO_PREVIEW_REASON))
        }
        openItem(itemId)
        openPreviewPane(target)
        announce(keepExistingContent ? UNAVAILABLE_REASON : NO_PREVIEW_REASON)
        return
      }

      // A failed click must not immediately retry as a background refresh.
      attemptedRefsRef.current.add(key)
      const request = beginWorkbenchPreviewRequest(contentPreviewRef, disableLoadingState)
      requestRef.current = request
      openPreviewPane(target)
      enableLoadingState()

      const path = artifact.absolutePath
      const itemId = getArtifactPreviewItemId(artifact)
      // Closing an already-open tab does not cancel the read, so the result must
      // not reopen it. The content is still cached for the next click.
      const wasOpen = latestOpenItemsRef.current?.includes(itemId) ?? false
      const closedWhilePending = () => wasOpen && !latestOpenItemsRef.current?.includes(itemId)
      // A newer ref, captured content, or a different session replaced this one
      // while the read was pending, so open whatever the path now resolves to.
      const reopenIfSuperseded = () => {
        if (!isReadSuperseded(path, key)) return false
        const latest = latestArtifactsByPathRef.current.get(path)
        if (latest && (isInWorkbench || latest.content !== undefined || latestRefKeysByPathRef.current.has(path))) {
          void openArtifactAtRef.current?.(latest, target)
          // A ref read announces itself, but captured content opens immediately.
          if (latest.content !== undefined) announce(openedMessage(latest))
        } else if (!latestOpenItemsRef.current?.length) {
          // Nothing can replace this read, so do not leave the pane open and empty.
          closePreviewPane?.()
        }
        return true
      }

      try {
        const preflightReason = refPreflightReason(artifact)
        if (preflightReason) throw new ArtifactPreviewError(preflightReason)
        const result = await readResource(artifact.contentRef.uri)
        request.finish()
        if (request.signal.aborted) return
        if (closedWhilePending()) {
          if (!isReadSuperseded(path, key)) cacheContent(key, decodeArtifactResource(result, artifact))
          return
        }
        if (reopenIfSuperseded()) return
        const content = decodeArtifactResource(result, artifact)
        cacheContent(key, content)
        const resolvedItem = buildArtifactPreviewItem({...artifact, content}, new Date(), undefined, {
          catalog: [...latestArtifactsByPathRef.current.values()],
          writesSettled: latestArtifactWritesSettledRef.current,
        })
        if (!resolvedItem) return
        updateItem(resolvedItem)
        openItem(resolvedItem.id)
        announce(openedMessage(artifact))
      } catch (error) {
        request.finish()
        if (request.signal.aborted || closedWhilePending() || reopenIfSuperseded()) return
        const reason = error instanceof ArtifactPreviewError ? error.reason : UNAVAILABLE_REASON
        // A background read of the same ref may have succeeded in the meantime.
        const cached = contentCacheRef.current.get(key)
        const cachedItem =
          cached === undefined
            ? undefined
            : buildArtifactPreviewItem({...artifact, content: cached}, new Date(), undefined, {
                catalog: [...latestArtifactsByPathRef.current.values()],
                writesSettled: latestArtifactWritesSettledRef.current,
              })
        if (cachedItem) {
          updateItem(cachedItem)
          openItem(cachedItem.id)
          announce(openedMessage(artifact))
          return
        }
        // Keep an older version that is still open rather than blanking it.
        const existingItem = latestOpenItemsRef.current?.includes(itemId)
          ? latestItemsRef.current?.get(itemId)
          : undefined
        if (existingItem?.type === 'file' && !existingItem.unavailableReason) {
          openItem(existingItem.id)
          announce(reason)
          return
        }
        const unavailableItem = buildUnavailableArtifactPreviewItem(artifact, new Date(), reason)
        updateItem(unavailableItem)
        openItem(unavailableItem.id)
        announce(reason)
      }
    },
    [
      cacheContent,
      closePreviewPane,
      contentPreviewRef,
      disableLoadingState,
      enableLoadingState,
      isInWorkbench,
      isReadSuperseded,
      openItem,
      openPreviewPane,
      readResource,
      refKeysByPath,
      updateItem,
    ],
  )

  useLayoutEffect(() => {
    openArtifactAtRef.current = openArtifactAt
  }, [openArtifactAt])

  const openArtifact = useCallback(
    (artifact: Artifact, event: React.MouseEvent) => openArtifactAt(artifact, event.currentTarget as HTMLElement),
    [openArtifactAt],
  )

  // An open tab whose artifact moved to a newer ref is refreshed quietly. The
  // read only fills the cache, and useSyncOpenArtifactPreviews pushes resolved
  // content into the tab. A failed read leaves the older content in place.
  const cachedValues = contentByRef
  useEffect(() => {
    if (!readResource || !allOpenItems || allOpenItems.length === 0) return
    const openIds = new Set(allOpenItems)
    const generation = backgroundGenerationRef.current

    for (const artifact of artifacts) {
      if (artifact.content !== undefined || !artifact.contentRef) continue
      if (!openIds.has(getArtifactPreviewItemId(artifact))) continue
      const key = refKeysByPath.get(artifact.absolutePath)
      if (!key || cachedValues.has(key) || attemptedRefsRef.current.has(key)) continue
      if (refPreflightReason(artifact)) continue

      attemptedRefsRef.current.add(key)
      const path = artifact.absolutePath
      const uri = artifact.contentRef.uri
      void (async () => {
        try {
          const result = await readResource(uri)
          if (backgroundGenerationRef.current !== generation) return
          // The artifact was removed or received a newer ref while this read was pending.
          if (isReadSuperseded(path, key)) return
          cacheContent(key, decodeArtifactResource(result, artifact))
        } catch {
          // The tab keeps its older content, and clicking the row retries the read.
        }
      })()
    }
  }, [allOpenItems, artifacts, cacheContent, cachedValues, isReadSuperseded, readResource, refKeysByPath])

  useSyncOpenArtifactPreviews(resolvedArtifacts, historyIsSettled, refPaths, isInWorkbench, artifactWritesSettled)

  return {
    artifacts: resolvedArtifacts,
    canOpenArtifact,
    cancelPendingOpen,
    openArtifact: contentPreview ? openArtifact : undefined,
  }
}
